/**
 * Environment detection utilities
 *
 * Centralized location for checking application environment.
 * Used for dev-only features and conditional behavior.
 *
 * Related: Issue #280 - Dev mode admin access bypass
 */

/**
 * Check if running in development environment
 * @returns true if NODE_ENV is development
 */
export function isDevelopment(): boolean {
  return import.meta.env.DEV
}

/**
 * Check if running in production environment
 * @returns true if NODE_ENV is production
 */
export function isProduction(): boolean {
  return import.meta.env.PROD
}

/**
 * POLICY: Dev Tools Access Control
 *
 * Single source of truth for who can access dev tools (/dev/* routes, DevToolsMenu, etc.)
 *
 * Current Policy Options:
 * 1. OPEN: Anyone can access (for early testing/debugging in production)
 * 2. ADMIN_ONLY: Only admins can access in production
 * 3. DEV_ONLY: Only in development mode (strictest)
 *
 * Configuration:
 * - Set VITE_DEV_TOOLS_ACCESS env var to: 'open' | 'admin-only' | 'dev-only'
 * - Default: 'open' (temporary policy for early production testing)
 *
 * @param isAdmin - Whether the current user is an admin
 * @returns true if dev tools should be accessible
 */
export function shouldShowDevTools(isAdmin: boolean = false): boolean {
  // Check environment variable for policy override
  const policy = import.meta.env.VITE_DEV_TOOLS_ACCESS || 'open'

  switch (policy) {
    case 'dev-only':
      // Strictest: Only in development mode
      return isDevelopment()

    case 'admin-only':
      // Admin access in production, everyone in dev
      return isDevelopment() || isAdmin

    case 'open':
    default:
      // Most permissive: Always accessible (current temporary policy)
      return true
  }
}
